import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './ReadFlights.css'; // Import the CSS
import GraphPlaceholder, { fetchGraphDataCached } from './GraphPlaceholder';
import Select from 'react-select';
import UploadTelemetryModal from './UploadTelemetryModal';
import UploadVideoModal from './UploadVideoModal';
import SynchronizedVideo from './SynchronizedVideo';
import { authFetch, isInteractiveAuthError } from '../authFetch';

// Prefer environment variable, otherwise default to the deployed API endpoint.
const READ_FLIGHTS_API_ENDPOINT = process.env.REACT_APP_READ_FLIGHTS_API_ENDPOINT ||
  'https://whzy23elj2.execute-api.eu-west-2.amazonaws.com/prod/readFlightData';

// Endpoint that returns flight summary rows (uuid, name, location, date)
// Replace the fallback URL with your deployed API once the Lambda is live.
const FLIGHT_SUMMARIES_API_ENDPOINT = process.env.REACT_APP_FLIGHT_SUMMARIES_API_ENDPOINT ||
  'https://78q545moi7.execute-api.eu-west-2.amazonaws.com/flightSummaries-dev';

// Default placeholder list until real columns are fetched.
export const PLACEHOLDER_TELEMETRY_OPTS = [
  { value: 'vehicle_global_position.alt', label: 'Altitude' },
  { value: 'vehicle_attitude.roll', label: 'Roll' },
  { value: 'vehicle_attitude.pitch', label: 'Pitch' },
  { value: 'vehicle_attitude.yaw', label: 'Yaw' },
  { value: 'vehicle_local_position.x', label: 'Local Position X' },
  { value: 'vehicle_local_position.y', label: 'Local Position Y' },
  { value: 'vehicle_local_position.z', label: 'Local Position Z' },
  { value: 'vehicle_global_position.lat', label: 'Latitude' },
  { value: 'vehicle_global_position.lon', label: 'Longitude' },
  { value: 'vehicle_air_data.indicated_airspeed_m_s', label: 'Airspeed' },
];

// Column data will be included directly in the flight summaries response – no extra endpoint needed.

function ReadFlights() {
  // We only want heartbeat auto-select logic to run on the dedicated graphs page.
  // Assumption: any route whose pathname contains the substring 'graph' (case-insensitive)
  // represents the graphs/visualisation area. Adjust this heuristic if your routing differs.
  const location = useLocation();
  const isGraphsPage = /graph/i.test(location.pathname);
  const [flights, setFlights] = useState([]);
  const [expandedLogs, setExpandedLogs] = useState({});
  const [nextToken, setNextToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allFlightsLoaded, setAllFlightsLoaded] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [graphs, setGraphs] = useState([]);
  const [videos, setVideos] = useState([]); // {id,url}
  const [globalTime, setGlobalTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const INPUT_HEIGHT = 30; // pixels – keep all inputs consistent

  const [selectedSeries, setSelectedSeries] = useState([]);

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: INPUT_HEIGHT,
      height: INPUT_HEIGHT,
      borderRadius: 0,
      borderColor: '#000',
      boxShadow: 'none',
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0 4px',
      height: INPUT_HEIGHT - 2, // subtract border
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: INPUT_HEIGHT - 2,
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: '0 4px',
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: '0 4px',
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#e6e6e6',
      borderRadius: 0,
    }),
    multiValueLabel: (base) => ({
      ...base,
      fontSize: '0.7rem',
      padding: '0 2px',
    }),
    multiValueRemove: (base) => ({
      ...base,
      padding: '0 2px',
    }),
  };

  const [uploadMenuCard, setUploadMenuCard] = useState(null); // holds submissionId of open menu
  const [showTelemetryModal, setShowTelemetryModal] = useState(false);

  // --- Dropdown filter state ---
  const [filterStart, setFilterStart] = useState('');   // yyyy-mm-dd
  const [filterEnd, setFilterEnd] = useState('');       // yyyy-mm-dd
  const [flightOptions, setFlightOptions] = useState([]); // dropdown options
  const [selectedFlight, setSelectedFlight] = useState(''); // chosen flight uuid
  const [userHasSelectedFlight, setUserHasSelectedFlight] = useState(false); // track manual selection to avoid overriding

  const [telemetryOptions, setTelemetryOptions] = useState(PLACEHOLDER_TELEMETRY_OPTS);

  // Update telemetry options whenever the selected flight changes or new summaries are fetched
  useEffect(() => {
    if (!selectedFlight) {
      setTelemetryOptions(PLACEHOLDER_TELEMETRY_OPTS);
      return;
    }

    const chosen = flightOptions.find(o => o.value === selectedFlight);
    if (chosen && Array.isArray(chosen.columns) && chosen.columns.length) {
      setTelemetryOptions(chosen.columns.map(c => ({ value: c, label: c })));
    } else {
      setTelemetryOptions([]);
    }
  }, [selectedFlight, flightOptions]);

  // Prefetch graph data as soon as user selects series
  useEffect(() => {
    if (!selectedFlight || selectedSeries.length === 0) return;
    const cols = selectedSeries.map(o => o.value);
    // Fire and forget; errors logged in cache helper
    fetchGraphDataCached(selectedFlight, cols).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFlight, selectedSeries]);

  const fetchFlights = useCallback(async (token) => {
    setIsLoading(true);
    setError(null);
    let url = READ_FLIGHTS_API_ENDPOINT;
    if (token) {
      url += `?nextToken=${encodeURIComponent(token)}`;
    }

    try {
      const response = await authFetch(url);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to fetch flights and parse error JSON.' }));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      // Ensure we only attempt to parse JSON if the server returns it
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Unexpected response (content-type: ${contentType}). First 200 chars: ${text.slice(0, 200)}`);
      }

      const data = await response.json();
      console.log('[ReadFlights][API Response DEBUG]', {
        totalItems: data.items?.length || 0,
        nextToken: data.nextToken,
        firstItemPreview: data.items?.[0] ? {
          uuid: data.items[0].uuid,
          submissionTS: data.items[0].submissionTS,
          formDataKeys: Object.keys(data.items[0].formData || {}),
          formDataTemplateId: data.items[0].formData?.templateId,
          hasFormDataFormData: !!data.items[0].formData?.formData
        } : null
      });
      
      // Assuming API returns { items: [...], nextToken: "..." }
      // And each item in 'items' has: uuid (for submissionId), templateId, submissionTS, formData (for data)
      // Adjust these keys if your API's actual response is different.
      const newFlights = data.items.map((item, index) => {
        console.log(`[ReadFlights][Raw API Item ${index}]:`, {
          uuid: item.uuid,
          submissionTS: item.submissionTS,
          formData: item.formData,
          directTemplateId: item.templateId // Check if templateId is at root level
        });

        if (!item || typeof item.uuid === 'undefined' || typeof item.submissionTS === 'undefined') {
          console.error(`Critical data missing or invalid in API response item [${index}] (uuid or submissionTS):`, item);
          return null;
        }

        const outerFormData = item.formData || {};
        const sectionsArr = Array.isArray(outerFormData.sections) ? outerFormData.sections : null;
        const legacyFormData = outerFormData.formData && typeof outerFormData.formData === 'object' ? outerFormData.formData : null;

        const formTitle = outerFormData.formTitle || 'Untitled Form';
        const submittedBy = outerFormData.submittedBy || 'Unknown User';
        
        // Extract template info from formData
        const templateId = outerFormData.templateId || null;
        const templateVersion = outerFormData.templateVersion || null;

        console.log(`[ReadFlights][Template Extraction ${index}]:`, {
          outerFormDataTemplateId: outerFormData.templateId,
          extractedTemplateId: templateId,
          templateVersion: templateVersion,
          outerFormDataKeys: Object.keys(outerFormData)
        });

        const flightUuidFromItem = outerFormData.flight_uuid || (legacyFormData && legacyFormData.flight_uuid) || item.flight_uuid || null;

        return {
          submissionId: item.uuid,
          submittedAt: parseInt(String(item.submissionTS), 10),
          formTitle,
          submittedBy,
          sections: sectionsArr,      // new structured sections
          data: legacyFormData,       // fallback legacy flat data
          flightUuid: flightUuidFromItem,
          templateId: templateId,
          templateVersion: templateVersion,
        };
      }).filter(flight => flight !== null); // Filter out any nulls from invalid items

      setFlights(prevFlights => token ? [...prevFlights, ...newFlights] : newFlights);
      setNextToken(data.nextToken);
      if (!data.nextToken) {
        setAllFlightsLoaded(true);
      }
    } catch (err) {
      if (isInteractiveAuthError(err)) return;
      console.error('Error fetching flights:', err);
      setError(err.message);
      setAllFlightsLoaded(true); // Prevent further loading attempts on error
    } finally {
      setIsLoading(false);
      if (isInitialLoad) setIsInitialLoad(false);
    }
  }, [isInitialLoad]);

  useEffect(() => {
    fetchFlights(null); // Initial fetch
  }, [fetchFlights]);

  const handleLoadMore = () => {
    if (nextToken && !isLoading) {
      fetchFlights(nextToken);
    }
  };

  const formatDateDDMMYYYY = (dateString) => {
    if (!dateString) return '';
    const dateObj = new Date(dateString);
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const toggleLogExpansion = (submissionId) => {
    setExpandedLogs(prev => ({
      ...prev,
      [submissionId]: !prev[submissionId]
    }));
  };

  const getFieldValue = (key, value) => {
    if (typeof value === 'boolean') {
      return value ? <span className="boolean-true">Yes</span> : <span className="boolean-false">No</span>;
    }
    if (value === undefined || value === null || String(value).trim() === '') {
      return <span style={{ fontStyle: 'italic', color: '#999' }}>Not provided</span>;
    }
    if (typeof value === 'object') {
      return JSON.stringify(value); // Basic display for nested objects
    }
    return value;
  };

  // Returns the Location from the "General" section; otherwise "No location"
  const getLocationFromSections = (submission) => {
    if (!submission || !Array.isArray(submission.sections)) return 'No location';
    const general = submission.sections.find(
      (s) => (s.sectionLabel || '').toLowerCase() === 'general'
    );
    if (!general || !Array.isArray(general.fields)) return 'No location';
    const locField = general.fields.find(
      (f) => (f.label || '').toLowerCase() === 'location'
    );
    const value = locField && locField.value;
    if (value === undefined || value === null || String(value).trim() === '') return 'No location';
    return String(value);
  };

  // Returns the exact "Test name" from the "General" section; otherwise "No test name"
  const getTestNameFromSections = (submission) => {
    if (!submission || !Array.isArray(submission.sections)) return 'No test name';
    const general = submission.sections.find(
      (s) => (s.sectionLabel || '').toLowerCase() === 'general'
    );
    if (!general || !Array.isArray(general.fields)) return 'No test name';
    const field = general.fields.find(
      (f) => (f.label || '') === 'Test name'
    );
    const value = field && field.value;
    if (value === undefined || value === null || String(value).trim() === '') return 'No test name';
    return String(value);
  };

  const handleCreateGraph = () => {
    const fieldsArr = selectedSeries.map(o => o.value);
    if (!selectedFlight || fieldsArr.length === 0) return;
    setGraphs(prev => [...prev, { id: Date.now(), flightUuid: selectedFlight, columns: fieldsArr }]);
    setSelectedSeries([]);
  };

  const [showVideosPane, setShowVideosPane] = useState(false);

  const handleAddVideo = () => {
    if (!selectedFlight) {
      alert('Select a flight first');
      return;
    }
    if (!showVideosPane) {
      fetchVideos(selectedFlight);
      setShowVideosPane(true);
    } else {
      fetchVideos(selectedFlight);
    }
  };

  const handleRemoveVideo = (id) => {
    setVideos(vs => vs.filter(v => v.id !== id));
  };

  const handleUserSeek = (newGlobalTime) => {
    setGlobalTime(newGlobalTime);
  };

  const handleUserPlayPause = (p) => {
    setPlaying(p);
  };

  const handleDownloadClick = async () => {
    try {
      const response = await authFetch('https://vrw0nl99e2.execute-api.eu-west-2.amazonaws.com/prod/readFlightLogSummaryData');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const filename = response.headers.get('content-disposition')?.split('filename=')[1]?.replace(/"/g, '') || 'data.csv';

  const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      if (isInteractiveAuthError(error)) return;
      console.error('Download failed:', error);
      alert('Failed to download data. Please check the console for more details.');
    }
  };

  // Removed openTelemetryModal - now handled inline with context setting

  const closeTelemetryModal = () => setShowTelemetryModal(false);

  // Fetch flight summaries whenever filters (or auto-select state) change
  useEffect(() => {
    const fetchSummaries = async () => {
      try {
        let url = FLIGHT_SUMMARIES_API_ENDPOINT;
        const qs = [];
        if (filterStart) qs.push(`startDate=${encodeURIComponent(filterStart)}`);
        if (filterEnd)   qs.push(`endDate=${encodeURIComponent(filterEnd)}`);
        if (qs.length) url += `?${qs.join('&')}`;

        const resp = await authFetch(url);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const rows = await resp.json();

        const nowSec = Date.now() / 1000;
        const toSeconds = (val) => {
          if (val == null) return null;
          if (typeof val === 'number') {
            if (!Number.isFinite(val)) return null;
            return val > 1e12 ? val / 1000 : val; // treat large as ms epoch
          }
          if (typeof val === 'string') {
            const num = Number(val);
            if (Number.isFinite(num)) return num > 1e12 ? num / 1000 : num;
            const d = Date.parse(val);
            if (!isNaN(d)) return d / 1000;
            return null;
          }
          return null;
        };

        let bestLive = null; // { flight_uuid, hbSec, delta }
        if (isGraphsPage) {
          rows.forEach(r => {
            const hbRaw = r.last_hearbeat ?? r.last_heartbeat ?? r.lastHeartbeat;
            const flightName = r.flight_name || r.flightName || 'Unknown';
            if (!/trial/i.test(flightName)) return; // only consider flights whose name contains 'Trial'
            const hbSec = toSeconds(hbRaw);
            if (hbSec != null) {
              const delta = nowSec - hbSec;
              const deltaRounded = Number.isFinite(delta) ? Number(delta.toFixed(1)) : null;
              const status = delta > 30 ? 'Dead Flight' : 'Live Flight';
              try { console.log(`[Heartbeat] name="${flightName}" delta=${deltaRounded}s status=${status}`); } catch(e) {}
              if (status === 'Live Flight') {
                if (!bestLive || hbSec > bestLive.hbSec) {
                  bestLive = { flight_uuid: r.flight_uuid || r.flightUuid || r.uuid, hbSec, delta };
                }
              }
            } else if (isGraphsPage) { // only log absence on graphs page
              try { console.log(`[Heartbeat] name="${flightName}" no valid heartbeat value`); } catch(e) {}
            }
          });
        }

        const opts = rows.map(r => {
          const datePart = r.flight_date ? String(r.flight_date).slice(0, 10) : 'Unknown';
            const namePart = r.flight_name || 'Unknown';
            const locPart  = r.flight_location || 'Unknown';
            return {
              value: r.flight_uuid,
              label: `${namePart} - ${locPart} - ${datePart}`,
              columns: Array.isArray(r.columns) ? r.columns : [],
            };
        });
        setFlightOptions(opts);

        // Auto-select the most recent live flight if user hasn't made a manual choice yet
        if (isGraphsPage && !userHasSelectedFlight && !selectedFlight && bestLive && bestLive.flight_uuid) {
          setSelectedFlight(bestLive.flight_uuid);
          try { console.log('[Heartbeat] Auto-selected live flight', bestLive.flight_uuid, 'delta_s=', Number(bestLive.delta.toFixed(1))); } catch(e) {}

          // Optionally auto-create default graphs if none exist
          if (graphs.length === 0) {
            const defaultPref = ['velocity','altitude','battery'];
            const flightOpt = opts.find(o => o.value === bestLive.flight_uuid);
            const available = flightOpt?.columns || [];
            let chosen = defaultPref.filter(c => available.includes(c));
            if (chosen.length === 0) chosen = available.slice(0, 3);
            if (chosen.length) {
              setGraphs([{ id: Date.now(), flightUuid: bestLive.flight_uuid, columns: chosen }]);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching flight summaries:', err);
        setFlightOptions([]);
      }
    };
    fetchSummaries();
  }, [filterStart, filterEnd, userHasSelectedFlight, selectedFlight, graphs.length, isGraphsPage]);

  const handleRemoveGraph = (gid) => {
    setGraphs(prev => prev.filter(g => g.id !== gid));
  };

  const [showVideoModal, setShowVideoModal] = useState(false);
  const navigate = useNavigate();
  const [modalFlightUuid, setModalFlightUuid] = useState('');

  const fetchVideos = useCallback(async (flightUuid) => {
    if (!flightUuid) return;
    try {
      const res = await authFetch(`https://0ynzhe2caa.execute-api.eu-west-2.amazonaws.com/listFlightVideos-dev?flight_uuid=${flightUuid}`);
      if (!res.ok) throw new Error('failed');
      const list = await res.json();
      console.log('[VideoSync] fetched videos', list);
      setVideos(list.map(v => ({ id: v.video_uuid, url: v.url, offset: v.video_offset_s || 0 })));

      // If this is the first load and backend provided launch offset, jump timeline to it
      if (!showVideosPane && list.length && list[0].launch_offset_s != null) {
        setGlobalTime(list[0].launch_offset_s);
      }
    } catch (e) {
      console.error(e);
    }
  }, [showVideosPane]);

  // Reset videos display when flight changes
  useEffect(() => {
    setShowVideosPane(false);
    setVideos([]);
  }, [selectedFlight]);

  // Auto-load videos when a flight is selected
  useEffect(() => {
    if (selectedFlight) {
      console.log('[ReadFlights] Auto-loading videos for flight:', selectedFlight);
      fetchVideos(selectedFlight);
      setShowVideosPane(true); // Automatically show the video pane
    }
  }, [selectedFlight, fetchVideos]);

  if (isInitialLoad && isLoading) {
    return <div className="read-flights-container"><p className="loading-message">Loading flight logs...</p></div>;
  }

  if (error && flights.length === 0) { // Show error prominently if initial load fails
    return <div className="read-flights-container"><p className="error-message">Error: {error}</p></div>;
  }

  if (flights.length === 0 && !isLoading) {
    return (
      <div className="read-flights-container">
        <div className="no-submissions">No flight logs found.</div>
      </div>
    );
  }

  return (
    <>
      <div className="sub-navbar" style={{ position:'relative' }}>
        <div className="sub-actions">
          <button className="sub-btn" onClick={handleDownloadClick}>Download</button>
        </div>
      </div>

      <div className="read-content-wrapper">
        <div className="read-page-container">
          {/* Right Pane – Prior Flights */}
          <div className="right-pane">
            <div className="read-flights-container">
              <h1>Prior Notes</h1>
              {error && <p className="error-message">Error fetching more flights: {error}</p>}
              {flights.map(submission => {
                const isExpanded = !!expandedLogs[submission.submissionId];
                const title = submission.formTitle || `Log ID: ${submission.submissionId}`;
                const locationText = getLocationFromSections(submission);
                const testName = getTestNameFromSections(submission);

                return (
                  <div key={submission.submissionId} className={`submitted-form-card ${isExpanded ? 'expanded' : 'collapsed'}`}>
                    <div className="form-card-header" onClick={() => toggleLogExpansion(submission.submissionId)}>
                      <span className="test-name">{testName}</span>
                      <div className="header-right-content">
                        <div className="header-right-stack" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}>
                          <span className="submission-date">{formatDateDDMMYYYY(submission.submittedAt)}</span>
                          <span className="submission-date">{locationText}</span>
                        </div>
                        <button
                          className="sub-btn"
                          title="Edit this log"
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('🔄 EDIT BUTTON CLICKED from ReadFlights');
                            console.log('📝 Submission being passed to edit mode:', submission);
                            console.log('🎯 Navigation state being passed:', {
                              mode: 'flights',
                              edit: true,
                              submission: submission,
                            });
                            // Navigate to write page in edit mode, passing the full submission payload
                            navigate('/flight_tracker/write', {
                              state: {
                                mode: 'flights',
                                edit: true,
                                submission: submission,
                              },
                            });
                            console.log('✅ Navigation to edit mode initiated');
                          }}
                          style={{ 
                            marginLeft: '8px',
                            backgroundColor: '#000000',
                            color: '#ffffff',
                            border: 'none',
                            outline: 'none',
                            padding: '4px 8px'
                          }}
                        >
                          Edit
                        </button>
                        <span
                          className={`expansion-indicator ${isExpanded ? 'expanded' : 'collapsed'}`}
                          style={{ marginLeft: '8px', marginRight: '8px' }}
                        >
                          {isExpanded ? '▾' : '▸'}
                        </span>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="form-card-body">
                        {/* Upload toolbar */}
                        <div className="upload-toolbar">
                          <button className="upload-menu-btn" onClick={(e)=>{e.stopPropagation(); setUploadMenuCard(uploadMenuCard===submission.submissionId? null: submission.submissionId);}}>Upload ▾</button>
                          {uploadMenuCard===submission.submissionId && (
                          <div className="upload-dropdown">
                            <button className="sub-btn" style={{ display:'block', width:'100%' }} onClick={(e)=>{
                              e.stopPropagation(); 
                              const contextToSet = { 
                                submissionId: submission.submissionId, 
                                templateId: submission.submissionId, // Use submission UUID for flight relationship
                                flightUuid: submission.flightUuid || null 
                              };
                              console.log('[ReadFlights][Telemetry Button Click] Setting upload context:', {
                                submissionId: submission.submissionId,
                                oldTemplateIdSlug: submission.templateId,
                                newTemplateIdUuid: submission.submissionId,
                                flightUuid: submission.flightUuid,
                                finalContext: contextToSet
                              });
                              setUploadMenuCard(contextToSet); 
                              setShowTelemetryModal(true);
                            }}>Telemetry</button>
                            <button className="sub-btn" style={{ display:'block', width:'100%' }} onClick={(e)=>{e.stopPropagation(); setModalFlightUuid(submission.flightUuid || submission.flight_uuid || ''); setShowVideoModal(true); setUploadMenuCard(null);}}>Video</button>
                          </div>
                          )}
                        </div>

                        <div className="form-field-display timestamp-detail">
                          <strong>Template Name:</strong>
                          <span>{title}</span>
                        </div>
                        {submission.templateVersion && (
                          <div className="form-field-display timestamp-detail">
                            <strong>Template Version:</strong>
                            <span>v{submission.templateVersion}</span>
                          </div>
                        )}
                        <div className="form-field-display timestamp-detail">
                          <strong>Full Submitted Time:</strong>
                          <span>{new Date(submission.submittedAt).toLocaleString()}</span>
                        </div>
                        <div className="form-field-display">
                          <strong>Submitted By:</strong>
                          <span>{submission.submittedBy || 'N/A'}</span>
                        </div>
                        {submission.sections ? (
                          submission.sections.map((sec, idx) => (
                            <React.Fragment key={idx}>
                              <hr className="read-section-divider" />
                              <div className="read-section-title">{sec.sectionLabel}</div>
                              {sec.fields.map((f, fi) => (
                                <div key={fi} className="form-field-display">
                                  <strong>{f.label}:</strong>
                                  <span>{getFieldValue(f.label, f.value)}</span>
                                </div>
                              ))}
                            </React.Fragment>
                          ))
                        ) : (
                          submission.data && Object.entries(submission.data).map(([key, value]) => {
                            const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());

                            if (/Section$/i.test(key) && (value === undefined || value === null || String(value).trim() === '')) {
                              return (
                                <React.Fragment key={key}>
                                  <hr className="read-section-divider" />
                                  <div className="read-section-title">{label}</div>
                                </React.Fragment>
                              );
                            }

                            return (
                              <div key={key} className="form-field-display">
                                <strong>{label}:</strong>
                                <span>{getFieldValue(key, value)}</span>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {!allFlightsLoaded && !isLoading && (
                <button onClick={handleLoadMore} className="load-more-button">
                  More Flights
                </button>
              )}
              {isLoading && !isInitialLoad && <p className="loading-message">Loading more flights...</p>}
            </div>
          </div>

          {/* Left Pane – Placeholder for graphs */}
          <div className="left-pane">
            {showVideosPane && videos.length>0 && (
              <div className="video-container">
                {videos.map(v => (
                  <div key={v.id} className="video-wrapper" style={{ marginBottom: '10px' }}>
                    <SynchronizedVideo
                      src={v.url}
                      currentTime={Math.max(0, globalTime - (v.offset || 0))}
                      playing={playing}
                      onUserSeek={(t)=>handleUserSeek(t + (v.offset || 0))}
                      onUserPlayPause={handleUserPlayPause}
                      offset={v.offset || 0}
                      globalTime={globalTime}
                      videoId={v.id}
                    />
                    <button className="video-remove-btn" onClick={()=>handleRemoveVideo(v.id)} title="Remove video">×</button>
                  </div>
                ))}
              </div>
            )}

            {graphs.length > 0 && (
              <div className="graphs-container">
                {graphs.map(g => (
                  <GraphPlaceholder
                    key={g.id}
                    id={g.id}
                    flightUuid={g.flightUuid}
                    columns={g.columns}
                    currentTime={globalTime}
                    playing={playing}
                    onTogglePlay={()=>setPlaying(p=>!p)}
                    onUserSeek={(t)=>handleUserSeek(t)}
                    onResetGlobalTime={(t)=>setGlobalTime(t)}
                    onRemove={() => handleRemoveGraph(g.id)}
                  />
                ))}
              </div>
            )}

            {/* Fixed box at bottom of left pane */}
            <div className="left-bottom-box">
              <div className="filter-group">
                <label>
                  Filter Start:&nbsp;
                  <input type="date" className="filter-input" value={filterStart} onChange={e => setFilterStart(e.target.value)} />
                </label>
                <label>
                  Filter End:&nbsp;
                  <input type="date" className="filter-input" value={filterEnd} onChange={e => setFilterEnd(e.target.value)} />
                </label>
              </div>

              <div className="filter-group">
                <label>
                  Select Flight:&nbsp;
                  <select className="filter-select" value={selectedFlight} onChange={e => { setUserHasSelectedFlight(true); setSelectedFlight(e.target.value); }}>
                    <option value="">-- Select Flight --</option>
                    {flightOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="filter-group">
                <label className="select-label">
                  Y Axis:
                  <div className="select-wrapper">
                    <Select
                      options={telemetryOptions}
                      isMulti
                      value={selectedSeries}
                      onChange={setSelectedSeries}
                      classNamePrefix="telemetry"
                      placeholder="Select Data"
                      styles={selectStyles}
                    />
                  </div>
                </label>
              </div>

              <div className="action-buttons">
                <button className="create-graph-button" type="button" onClick={handleCreateGraph}>Create Graph</button>
                <button className="toggle-video-button" type="button" onClick={handleAddVideo}>Refresh Videos</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <UploadTelemetryModal isOpen={showTelemetryModal} onClose={closeTelemetryModal} context={uploadMenuCard && typeof uploadMenuCard === 'object' ? uploadMenuCard : null} />
      <UploadVideoModal
        open={showVideoModal}
        defaultFlightUuid={modalFlightUuid || selectedFlight || ''}
        flightOptions={flightOptions}
        onClose={()=>setShowVideoModal(false)}
        onUploaded={()=>{
          const reloadUuid = modalFlightUuid || selectedFlight || '';
          if (reloadUuid) fetchVideos(reloadUuid);
          setShowVideosPane(true);
        }}
      />
    </>
  );
}

export default ReadFlights;
